import React, { useState, useEffect } from 'react';
import { 
  Users, Key, DollarSign, TrendingUp, Calendar, BarChart3, 
  Settings, UserPlus, CheckCircle, Clock, AlertTriangle, Shield 
} from 'lucide-react';
import { adminAPI, userAPI, accessKeyAPI } from '../../services/api';
import { formatCurrency } from '../../utils/calculations';
import MarketingStats from './MarketingStats';

const AdminDashboard = ({ setActiveTab }) => {
  const [showMarketingStats, setShowMarketingStats] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeKeys: 0,
    expiredKeys: 0,
    expiringKeys: 0,
    lifetimeKeys: 0,
    totalRevenue: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [revenueResponse, usersResponse, keysResponse] = await Promise.all([
        adminAPI.getRevenue(),
        userAPI.getAllUsers(),
        accessKeyAPI.getAllAccessKeys()
      ]);

      const users = usersResponse.map(user => ({
        ...user,
        key_expires_at: new Date(user.key_expires_at),
        created_at: new Date(user.created_at)
      }));

      const keys = keysResponse.map(key => ({
        ...key,
        expires_at: new Date(key.expires_at),
        created_at: new Date(key.created_at)
      }));

      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      
      console.log('🔍 DEBUG - AdminDashboard chaves:');
      console.log('📅 Data atual:', now);
      console.log('📊 Total de chaves:', keys.length);
      
      keys.forEach((key, index) => {
        console.log(`🔑 Chave ${index + 1}:`, {
          key: key.key,
          email: key.email,
          is_active: key.is_active,
          expires_at: key.expires_at,
          expires_at_string: key.expires_at.toString(),
          is_expired: key.expires_at <= now,
          days_remaining: Math.ceil((key.expires_at - now) / (1000 * 60 * 60 * 24))
        });
      });
      
      const activeKeys = keys.filter(key => key.is_active && key.expires_at > now);
      const expiredKeys = keys.filter(key => !key.is_active || key.expires_at <= now);
      const expiringKeys = keys.filter(key => 
        key.is_active && key.expires_at > now && key.expires_at <= sevenDaysFromNow
      );
      const lifetimeKeys = keys.filter(key => key.type === 'lifetime'); // If you have this field
      
      console.log('📊 Resultado dos filtros:');
      console.log('✅ Chaves ativas:', activeKeys.length);
      console.log('❌ Chaves expiradas:', expiredKeys.length);
      console.log('⚠️ Expirando em breve:', expiringKeys.length);
      console.log('♾️ Vitalícias:', lifetimeKeys.length);

      // Get recent users (last 5)
      const sortedUsers = users.sort((a, b) => b.created_at - a.created_at);
      const recent = sortedUsers.slice(0, 5);

      setStats({
        totalUsers: users.length,
        activeKeys: activeKeys.length,
        expiredKeys: expiredKeys.length,
        expiringKeys: expiringKeys.length,
        lifetimeKeys: lifetimeKeys.length,
        totalRevenue: revenueResponse.total_revenue
      });

      setRecentUsers(recent);
      console.log('✅ Dashboard data loaded');
    } catch (error) {
      console.error('❌ Erro ao carregar dashboard:', error);
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToUserManagement = () => {
    setActiveTab('manage-users');
  };

  const toggleMarketingStats = () => {
    setShowMarketingStats(!showMarketingStats);
  };

  const statCards = [
    {
      title: 'Total de Usuários',
      value: stats.totalUsers,
      icon: Users,
      color: 'blue',
      description: 'Usuários cadastrados'
    },
    {
      title: 'Chaves Ativas',
      value: stats.activeKeys,
      icon: Key,
      color: 'green',
      description: 'Licenças válidas'
    },
    {
      title: 'Chaves Expiradas',
      value: stats.expiredKeys,
      icon: Calendar,
      color: 'red',
      description: 'Licenças vencidas'
    },
    {
      title: 'Receita Estimada',
      value: formatCurrency(stats.totalRevenue),
      icon: DollarSign,
      color: 'green',
      description: 'Baseado em chaves ativas (R$ 30,00 cada)',
      isRevenue: true
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Dashboard Administrativo
          </h1>
        </div>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-gray-500">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Dashboard Administrativo
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Controle geral do sistema Stake Mind
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg">
            <Shield className="w-5 h-5" />
            <span className="font-semibold">Modo Administrador</span>
          </div>
          <button 
            onClick={toggleMarketingStats}
            className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-lg hover:from-green-600 hover:to-emerald-700 transition-colors flex items-center space-x-2 shadow-lg"
          >
            <BarChart3 className="w-5 h-5" />
            <span className="font-semibold">
              {showMarketingStats ? 'Ocultar Estatísticas de Marketing' : 'Ver Estatísticas de Marketing'}
            </span>
          </button>
        </div>
      </div>

      {/* Marketing Stats */}
      {showMarketingStats && (
        <MarketingStats onClose={() => setShowMarketingStats(false)} />
      )}

      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
            Chaves Ativas
          </h3>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {stats.activeKeys}
          </p>
          <p className="text-xs text-gray-500 mt-1">Usuários ativos no sistema</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
            Expirando em Breve
          </h3>
          <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
            {stats.expiringKeys}
          </p>
          <p className="text-xs text-gray-500 mt-1">Próximos 7 dias</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
            Chaves Expiradas
          </h3>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">
            {stats.expiredKeys}
          </p>
          <p className="text-xs text-gray-500 mt-1">Necessitam renovação</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          </div>
          <h3 className="text-gray-600 dark:text-gray-400 text-sm font-medium mb-1">
            Receita Estimada
          </h3>
          <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {formatCurrency(stats.totalRevenue)}
          </p>
          <p className="text-xs text-gray-500 mt-1">Baseado em chaves ativas</p>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Keys Summary */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Resumo de Chaves
            </h3>
            <Key className="w-5 h-5 text-gray-500" />
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-700 dark:text-gray-300">Chaves Ativas</span>
              </div>
              <span className="text-green-600 dark:text-green-400 font-bold text-lg">
                {stats.activeKeys}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <TrendingUp className="w-5 h-5 text-purple-500" />
                <span className="text-gray-700 dark:text-gray-300">Chaves Vitalícias</span>
              </div>
              <span className="text-purple-600 dark:text-purple-400 font-bold text-lg">
                {stats.lifetimeKeys}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-orange-500" />
                <span className="text-gray-700 dark:text-gray-300">Expirando (7 dias)</span>
              </div>
              <span className="text-orange-600 dark:text-orange-400 font-bold text-lg">
                {stats.expiringKeys}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <span className="text-gray-700 dark:text-gray-300">Chaves Expiradas</span>
              </div>
              <span className="text-red-600 dark:text-red-400 font-bold text-lg">
                {stats.expiredKeys}
              </span>
            </div>
          </div>
        </div>

        {/* Recent Users */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Usuários Recentes
            </h3>
            <Users className="w-5 h-5 text-gray-500" />
          </div>
          <div className="space-y-3">
            {recentUsers.length > 0 ? (
              recentUsers.map(user => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">
                        {user.name}
                      </p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-medium text-green-600 dark:text-green-400">
                      Ativo
                    </p>
                    <p className="text-xs text-gray-500">
                      {user.created_at.toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">Nenhum usuário recente</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Status do Sistema
          </h3>
          <Shield className="w-5 h-5 text-gray-500" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-gray-700 dark:text-gray-300">Sistema Online</span>
            </div>
            <span className="text-green-600 dark:text-green-400 font-medium">Ativo</span>
          </div>
          <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-center space-x-3">
              <Key className="w-5 h-5 text-blue-500" />
              <span className="text-gray-700 dark:text-gray-300">Autenticação</span>
            </div>
            <span className="text-blue-600 dark:text-blue-400 font-medium">Funcionando</span>
          </div>
          <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <div className="flex items-center space-x-3">
              <Users className="w-5 h-5 text-purple-500" />
              <span className="text-gray-700 dark:text-gray-300">Total Usuários</span>
            </div>
            <span className="text-purple-600 dark:text-purple-400 font-medium">
              {stats.totalUsers}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;